import { useCallback, useEffect, useRef, useState } from 'react';
import { type CurrentUser, currentUser, endImpersonation, logout } from './api.ts';
import { ScreenBoundary } from './components/ScreenBoundary.tsx';
import { SkipLink } from './components/SkipLink.tsx';
import { sectionsFor, TopBar, type View } from './components/TopBar.tsx';
import { applyPreferredLocale } from './i18n.ts';
import { watchIncoming } from './incoming-watch.ts';
import { AdminPage } from './pages/AdminPage.tsx';
import { ConsolePage } from './pages/ConsolePage.tsx';
import { DocumentsPage } from './pages/DocumentsPage.tsx';
import { DomainLinkPage } from './pages/DomainLinkPage.tsx';
import { HoursPage } from './pages/HoursPage.tsx';
import { InvitePage } from './pages/InvitePage.tsx';
import { LoginPage } from './pages/LoginPage.tsx';
import { ModerationPage } from './pages/ModerationPage.tsx';
import { NetworkPage } from './pages/NetworkPage.tsx';
import { NotificationsPage } from './pages/NotificationsPage.tsx';
import { OnboardingPage } from './pages/OnboardingPage.tsx';
import { PasswordResetPage } from './pages/PasswordResetPage.tsx';
import { PlatformPage, type PlatformSection } from './pages/PlatformPage.tsx';
import { ShiftsPage } from './pages/ShiftsPage.tsx';
import { SupervisionPage } from './pages/SupervisionPage.tsx';
import { WidgetPage } from './pages/WidgetPage.tsx';
import { disablePush } from './push.ts';
import { registerServiceWorker } from './service-worker.ts';

/**
 * Radice dell'applicazione.
 *
 * Nessun router: quattro sezioni e uno stato di accesso non giustificano una
 * dipendenza in più su un progetto che deve restare manutenibile da una
 * persona sola.
 *
 * La sezione corrente sta nel frammento dell'indirizzo e si scrive con
 * `history.replaceState`, **mai** `pushState`: dentro l'applicazione non si
 * aggiungono voci alla cronologia del browser (sezione 7ter). Vale anche per
 * lo staff, perché la stessa console può essere aperta sul computer di casa.
 */

const VIEWS: View[] = [
  'console',
  'shifts',
  'notifications',
  'onboarding',
  'hours',
  'supervision',
  'moderation',
  'admin',
  'widget',
  'platform',
  'network',
];

/**
 * Chi può stare su una sezione.
 *
 * Il server rifiuta comunque, ma una schermata che non si può usare non va
 * mostrata — nemmeno arrivandoci dall'indirizzo, che è l'unico modo per
 * arrivarci senza il pulsante.
 *
 * L'elenco è **lo stesso** che disegna la barra di navigazione: due elenchi che
 * devono dire la stessa cosa prima o poi divergono, e il modo in cui
 * divergerebbero è una voce raggiungibile a mano e non mostrata.
 */
function allowedView(view: View, user: CurrentUser | undefined): boolean {
  if (!user) return false;
  return sectionsFor(user).some((section) => section.id === view);
}

/** La prima sezione di questo soggetto: quella su cui si apre l'applicazione. */
function homeView(user: CurrentUser | undefined): View {
  return user ? (sectionsFor(user)[0]?.id ?? 'console') : 'console';
}

function readView(): View {
  const fragment = (globalThis.location?.hash ?? '').replace('#', '');
  return VIEWS.includes(fragment as View) ? (fragment as View) : 'console';
}

/**
 * Il token di un invito, quando l'indirizzo ne porta uno (`#invite=…`).
 *
 * Sta nel **frammento** e non nella query perché un frammento non viene inviato
 * a nessun server: non finisce nei nostri log delle richieste, né in quelli di
 * un proxy, né in un `Referer`. È un segreto che vale un accesso da
 * coordinatore, e la stessa attenzione che la sezione 7ter chiede per la
 * cronologia vale per dove passa (`docs/DECISIONI.md`, AK3).
 */
function readInviteToken(): string | undefined {
  const fragment = (globalThis.location?.hash ?? '').replace('#', '');
  if (!fragment.startsWith('invite=')) return undefined;
  const token = decodeURIComponent(fragment.slice('invite='.length));
  return token.length > 0 ? token : undefined;
}

/**
 * Toglie il token dall'indirizzo, senza aggiungere una voce alla cronologia.
 *
 * Un collegamento con dentro un invito già speso non serve più a niente, e
 * lasciarlo nella barra degli indirizzi vuol dire lasciarlo in un segnalibro,
 * in una cronologia, in uno screenshot (sezione 7ter).
 */
function clearInviteToken(): void {
  try {
    globalThis.history?.replaceState(null, '', globalThis.location?.pathname ?? '/');
  } catch {
    // Senza cronologia scrivibile non c'è niente da ripulire.
  }
}

/**
 * Il token di un reset password, quando l'indirizzo ne porta uno
 * (`#reset=…`). Stesso posto e stessa ragione del token dell'invito (AK3):
 * un frammento non viaggia verso nessun server.
 */
function readPasswordResetToken(): string | undefined {
  const fragment = (globalThis.location?.hash ?? '').replace('#', '');
  if (!fragment.startsWith('reset=')) return undefined;
  const token = decodeURIComponent(fragment.slice('reset='.length));
  return token.length > 0 ? token : undefined;
}

/**
 * Toglie il token del reset dall'indirizzo, come `clearInviteToken` e per lo
 * stesso motivo (sezione 7ter): un collegamento di reset già usato non deve
 * restare in un segnalibro, in una cronologia, in uno screenshot.
 */
function clearPasswordResetToken(): void {
  try {
    globalThis.history?.replaceState(null, '', globalThis.location?.pathname ?? '/');
  } catch {
    // Senza cronologia scrivibile non c'è niente da ripulire.
  }
}

/**
 * Il token del collegamento per il referente tecnico, quando l'indirizzo ne
 * porta uno (`#dns-link=…`). Stesso posto del token dell'invito e per lo
 * stesso motivo (AK3), ma **non si toglie** dall'indirizzo dopo la lettura:
 * a differenza dell'invito, questo collegamento non si consuma — chi lo ha
 * ricevuto può tornarci più volte finché il DNS non è a posto, ed è
 * esattamente l'uso previsto (`docs/DECISIONI.md`, AZ4).
 */
function readDomainLinkToken(): string | undefined {
  const fragment = (globalThis.location?.hash ?? '').replace('#', '');
  if (!fragment.startsWith('dns-link=')) return undefined;
  const token = decodeURIComponent(fragment.slice('dns-link='.length));
  return token.length > 0 ? token : undefined;
}

export function App() {
  const [user, setUser] = useState<CurrentUser>();
  const [checked, setChecked] = useState(false);
  const [view, setView] = useState<View>(readView);
  /**
   * Quale delle otto sezioni della piattaforma è aperta.
   *
   * Vive qui e non dentro `PlatformPage` perché il pulsante che la cambia
   * adesso sta nella barra laterale (`TopBar.tsx`, `docs/DECISIONI.md`,
   * BP2): due componenti diversi devono vedere lo stesso dato, quindi sale
   * al primo antenato comune. Irrilevante per chiunque non sia il fornitore
   * fuori da un ente, ma un solo stato in più non giustifica un contesto.
   */
  const [platformSection, setPlatformSection] = useState<PlatformSection>('tenants');
  /**
   * Il token si legge una volta e si **toglie subito** dall'indirizzo.
   *
   * Ripulirlo solo a percorso concluso voleva dire lasciarlo nella barra degli
   * indirizzi per tutto il tempo della compilazione — e per sempre, se chi lo
   * ha aperto abbandona o sbaglia la password. Segnalibro, cronologia,
   * screenshot: i tre casi che la sezione 7ter cita, sul telefono di chi lo
   * apre in riunione di direttivo (`docs/DECISIONI.md`, AM10).
   *
   * Il costo è che un ricaricamento perde l'invito. È preferibile a lasciarlo
   * scritto: il collegamento ce l'ha ancora chi lo ha aperto.
   */
  const [inviteToken, setInviteToken] = useState<string | undefined>(() => {
    const token = readInviteToken();
    if (token) clearInviteToken();
    return token;
  });
  const [domainLinkToken, setDomainLinkToken] = useState<string | undefined>(readDomainLinkToken);
  /** Stesso trattamento del token dell'invito: si legge una volta e si toglie subito. */
  const [resetToken, setResetToken] = useState<string | undefined>(() => {
    const token = readPasswordResetToken();
    if (token) clearPasswordResetToken();
    return token;
  });

  useEffect(() => {
    void currentUser()
      .then(setUser)
      .catch(() => setUser(undefined))
      .finally(() => setChecked(true));
  }, []);

  /**
   * Il titolo della sezione riceve il fuoco a ogni cambio.
   *
   * I pulsanti di navigazione sostituiscono tutto il contenuto ma lasciano il
   * fuoco dov'era: chi usa uno screen reader non saprebbe che la schermata è
   * cambiata, e chi naviga da tastiera si ritroverebbe a tabulare dall'inizio.
   */
  const headingRef = useRef<HTMLHeadingElement>(null);
  const previousView = useRef<View>(view);

  // Stabile fra un disegno e l'altro: la usa anche l'effetto che riporta alla
  // console quando squilla, e una funzione nuova a ogni render farebbe
  // ripartire quell'osservazione di continuo.
  const navigate = useCallback((next: View): void => {
    setView(next);
    try {
      globalThis.history?.replaceState(null, '', `#${next}`);
    } catch {
      // Senza cronologia scrivibile la sezione vale per questa scheda: non è
      // un errore da mostrare a nessuno.
    }
  }, []);

  /**
   * Il service worker si registra **dopo l'accesso**, non al caricamento.
   *
   * La console sta sullo stesso host da cui vive il widget dell'ente: chi
   * arriva su quella radice senza essere staff non deve ritrovarsi un service
   * worker e una cache sul dispositivo. Sono tracce che sopravvivono alla
   * cancellazione della cronologia, ed è esattamente ciò che la sezione 7ter
   * vuole evitare.
   */
  useEffect(() => {
    // Solo per chi risponde: il service worker serve alle notifiche di una
    // chiamata in arrivo, e il fornitore non ne riceve nessuna.
    if (user?.kind === 'volunteer') void registerServiceWorker();
  }, [user]);

  /**
   * La lingua del profilo, appena si sa di chi è il profilo.
   *
   * Prima dell'accesso la console non sa chi sta guardando e parte dalla
   * lingua predefinita; da qui in poi lo sa. Non scavalca una scelta fatta con
   * il selettore su questo dispositivo — è `applyPreferredLocale` a saperlo —
   * e non tocca niente se le due lingue coincidono, che è il caso normale.
   */
  useEffect(() => {
    // Una lingua non cambiata non è un errore da mostrare a nessuno, ma un
    // rifiuto non gestito sì: finirebbe in console senza dire niente a nessuno.
    void applyPreferredLocale(user?.preferredLocale).catch(() => undefined);
  }, [user]);

  /**
   * Fuori dalla console, una chiamata in arrivo riporta alla console.
   *
   * Il riquadro, il suono, l'annuncio e la scorciatoia per rispondere stanno
   * lì: da un'altra sezione una chiamata sarebbe muta, e passerebbe al
   * volontario successivo senza che nessuno se ne accorga. Proprio la prova di
   * notifica — che si fa al primo turno — porta in un'altra sezione.
   *
   * `navigate` scrive con `replaceState`: tornare alla console non aggiunge
   * una voce alla cronologia (sezione 7ter).
   */
  useEffect(() => {
    /**
     * Solo chi risponde alle chiamate.
     *
     * Il fornitore e il referente di rete non entrano in turno e non ricevono
     * proposte: interrogare la coda per conto loro sarebbe una richiesta ogni
     * pochi secondi che riceve 403, e in più — per il fornitore in assistenza —
     * una riga di registro accessi per una schermata che non sta guardando.
     */
    if (user?.kind !== 'volunteer' || view === 'console') return;
    return watchIncoming(() => navigate('console'));
  }, [user, view, navigate]);

  /** L'indirizzo può cambiare da fuori: avanti e indietro del browser, o a mano. */
  useEffect(() => {
    const onHashChange = (): void => {
      setView(readView());
      const token = readInviteToken();
      if (token) {
        clearInviteToken();
        setInviteToken(token);
      }
      // Stesso trattamento del token dell'invito, appena sopra: un collegamento
      // di reset incollato nella barra degli indirizzi mentre l'app è già
      // aperta deve funzionare quanto uno aperto a freddo (AK3).
      const reset = readPasswordResetToken();
      if (reset) {
        clearPasswordResetToken();
        setResetToken(reset);
      }
      setDomainLinkToken(readDomainLinkToken());
    };
    globalThis.addEventListener('hashchange', onHashChange);
    return () => globalThis.removeEventListener('hashchange', onHashChange);
  }, []);

  useEffect(() => {
    if (previousView.current === view) return;
    previousView.current = view;
    headingRef.current?.focus();
  }, [view]);

  const current = allowedView(view, user) ? view : homeView(user);

  if (!checked) return null;

  /**
   * L'invito viene **prima** di tutto il resto.
   *
   * Chi apre quel collegamento non ha ancora un accesso: mostrargli la
   * schermata di accesso vorrebbe dire chiedergli una password che non ha
   * ancora scelto. E viene prima anche di una sessione già aperta — su un
   * dispositivo condiviso, chi accetta l'invito può non essere chi è entrato
   * ieri.
   */
  if (inviteToken) {
    return <InvitePage token={inviteToken} onDone={() => setInviteToken(undefined)} />;
  }

  /**
   * Come l'invito: chi apre questo collegamento non ha ancora effettuato
   * l'accesso, e una sessione già aperta su un dispositivo condiviso non
   * deve nascondere il modulo a chi sta reimpostando la password di
   * qualcun altro.
   */
  if (resetToken) {
    return <PasswordResetPage token={resetToken} onDone={() => setResetToken(undefined)} />;
  }

  /**
   * Come l'invito, prima di tutto il resto: chi apre questo collegamento non
   * ha un accesso e non deve averne uno per leggere un record CNAME.
   */
  if (domainLinkToken) {
    return <DomainLinkPage token={domainLinkToken} />;
  }

  return (
    <>
      {/*
        Primo elemento focalizzabile: chi naviga da tastiera non deve
        attraversare l'intestazione a ogni pagina — e si porta dietro il
        contesto che quell'intestazione conteneva (`SkipLink`, AS5).
      */}
      <SkipLink tenantName={user?.tenant?.name} impersonating={Boolean(user?.impersonation)} />

      {user ? (
        <div className="app-shell">
          <TopBar
            user={user}
            view={current}
            onNavigate={navigate}
            platformSection={platformSection}
            onPlatformSectionChange={setPlatformSection}
            /*
              La sessione lato client si scarta comunque: un "esci" che non fa
              niente perché la rete è caduta è la cosa peggiore da lasciare in
              piedi su una console che si apre anche dal computer di casa.
            */
            /*
              Uscendo, questo dispositivo smette anche di ricevere notifiche.

              Senza, una console chiusa sul computer di casa — condiviso con la
              persona sbagliata — continuerebbe a mostrare "chiamata in arrivo"
              sulla schermata di blocco di chi non è più autenticato: la stessa
              minaccia della sezione 7ter, dal lato di chi risponde. Il server
              dimentica l'iscrizione solo se è **questa**, così uscire dal
              computer non spegne le notifiche sul telefono.
            */
            onSignedOut={() =>
              void disablePush()
                .catch(() => undefined)
                .then(() => logout().catch(() => undefined))
                .finally(() => setUser(undefined))
            }
            /*
              Uscire dall'ente in cui si è entrati per assistenza.

              Chiude la sessione impersonata e riporta all'accesso: per
              rientrare servono di nuovo password e secondo fattore, perché
              entrare in un ente è un atto e non uno stato in cui si resta
              (sezione 9bis).
            */
            onEndImpersonation={
              user.impersonation
                ? () =>
                    void endImpersonation()
                      .catch(() => undefined)
                      .finally(() => setUser(undefined))
                : undefined
            }
          />
          {/*
            Una sezione che cade non porta giù la console.

            Il confine sta **dentro** la barra, non intorno: se una schermata
            solleva, restano il nome dell'ente, la navigazione e il menu del
            profilo, cioè un modo di andare da un'altra parte che non sia
            ricaricare. Prima di questo, un'eccezione in un componente
            qualunque lasciava una pagina bianca a qualcuno che poteva avere
            una persona in linea.
          */}
          <div className="app-main">
            <ScreenBoundary resetKey={current}>
              {current === 'console' ? (
                <ConsolePage
                  user={user}
                  pageHeadingRef={headingRef}
                  onOpenNotifications={() => navigate('notifications')}
                />
              ) : null}
              {current === 'shifts' ? <ShiftsPage user={user} headingRef={headingRef} /> : null}
              {current === 'notifications' ? (
                <NotificationsPage user={user} headingRef={headingRef} />
              ) : null}
              {current === 'onboarding' ? (
                <OnboardingPage headingRef={headingRef} onNavigate={navigate} />
              ) : null}
              {current === 'hours' ? <HoursPage user={user} headingRef={headingRef} /> : null}
              {current === 'supervision' ? (
                <SupervisionPage user={user} headingRef={headingRef} />
              ) : null}
              {current === 'moderation' ? (
                <ModerationPage user={user} headingRef={headingRef} />
              ) : null}
              {current === 'admin' ? <AdminPage user={user} headingRef={headingRef} /> : null}
              {current === 'widget' ? <WidgetPage user={user} headingRef={headingRef} /> : null}
              {current === 'documents' ? (
                <DocumentsPage user={user} headingRef={headingRef} />
              ) : null}
              {current === 'platform' ? (
                <PlatformPage
                  user={user}
                  section={platformSection}
                  onSectionChange={setPlatformSection}
                  headingRef={headingRef}
                />
              ) : null}
              {current === 'network' ? <NetworkPage headingRef={headingRef} /> : null}
            </ScreenBoundary>
          </div>
        </div>
      ) : (
        <LoginPage onSignedIn={setUser} />
      )}
    </>
  );
}
